// Centralised post-auth navigation helpers.
//
// These exist so multiple call sites (sidebar logo, navbar logo, post-
// interview screen, login redirect) all agree on:
//   1. Where an authenticated user should land by default — which now
//      depends on the demo-practice flag + the user's role.
//   2. How to safely honour a `?_redirect=` query param without enabling
//      open redirects to attacker-controlled domains.
//
// Touching one place keeps the demo-flag wiring consistent and avoids
// each call site re-deriving the destination ad-hoc.

import { isOuraEnabled, isPlatformAdmin } from "@oura/role";
import { isReceptionistEnabled, RECEPTIONIST_COMPANY_TYPE } from "@receptionist/role";

const isDemoPracticeMode = (): boolean =>
    import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true";

/**
 * Returns the path an authenticated user should land on by default.
 *
 * - Demo-practice mode + job_seeker → "/interview"
 *   (the marketplace dashboard is hidden for this cohort)
 * - Oura roles → "/oura"; platform admins → "/oura/businesses"
 *   (both only while Oura is switched on here)
 * - Everyone else → "/dashboard"
 *
 * Pass the user's `role` when available so non-job_seeker users in demo
 * mode still get the standard /dashboard destination, and `company_type`
 * so a workspace-scoped product (Receptionist) can claim its own people
 * even when their role is a generic one.
 */
export const getPostAuthLandingPath = (
    role?: string | null,
    companyType?: string | null,
): string => {
    if (isDemoPracticeMode() && role === "job_seeker") {
        return "/browse";
    }
    // Only land an Oura user on /oura when the product is actually switched on
    // here. With the flag off in production the route guard sends them to
    // /unauthorized, so landing them there would be a redirect into a wall —
    // /dashboard resolves the same decision in one place instead.
    if ((role === "oura-staff" || role === "oura-client") && isOuraEnabled()) {
        return "/oura";
    }
    // Same rule for the Receptionist (ourteam-frontend#3612). A client lands on
    // their own bookings; staff have no single merchant — the JWT carries no
    // `receptionist.merchantId` for them — so a merchant-scoped screen would
    // have nothing to fetch, and they land on the roll-up instead.
    //
    // The workspace TYPE is checked as well as the role. A receptionist
    // workspace is a `companies` row with `type = "receptionist"`, and the
    // people in it are not all going to carry a receptionist role literal —
    // the workspace owner is an `admin` like any other. Landing them on
    // /dashboard put them in the recruiter product, which is not the thing
    // they bought. `/receptionist` is the generic door: ReceptionistHomeRedirect
    // sends staff to the roll-up and everyone else to the Overview (#4847), so
    // this does not have to know which they are.
    if (isReceptionistEnabled()) {
        if (role === "receptionist-client") return "/receptionist/home";
        if (role === "receptionist-staff") return "/receptionist/merchants";
        if (companyType === RECEPTIONIST_COMPANY_TYPE) return "/receptionist";
    }
    // Admins work in Oura first: their home is the business list, not the
    // recruitment dashboard. The same flag gate, so a build without Oura
    // still lands them on /dashboard rather than on a wall.
    if (isPlatformAdmin(role) && isOuraEnabled()) {
        return "/oura/businesses";
    }
    return "/dashboard";
};

/**
 * Returns a redirect path that's safe to navigate to.
 *
 * Only accepts same-origin relative paths (e.g. "/jobs", "/interview/123").
 * Protocol-relative URLs ("//evil.com/x") and absolute URLs
 * ("https://evil.com") are rejected and the caller's `fallback` is
 * returned instead. This prevents the `?_redirect=` query param from
 * becoming an open-redirect into attacker-controlled domains.
 */
export const sanitizeRedirectPath = (
    candidate: string | null | undefined,
    fallback: string
): string => {
    if (!candidate) return fallback;
    // Must start with a single slash and not a second slash (which would
    // be protocol-relative). Also reject backslashes that some browsers
    // normalise into slashes.
    if (!candidate.startsWith("/")) return fallback;
    if (candidate.startsWith("//") || candidate.startsWith("/\\")) return fallback;
    // Bail on anything that smells like a scheme. A relative path
    // never contains a colon in its first segment.
    const firstSegment = candidate.split("/", 2)[1] ?? "";
    if (firstSegment.includes(":")) return fallback;
    return candidate;
};
